/**
 * jrs-v2-cart-edit-trigger.js — Cart-page click → EventBus channel
 *
 * Phase 4 commit 4 (2026-05-21). Wires the cart-page Edit-customisation
 * button (rendered by commit 9 / Phase 4 session 3) to the cart-edit
 * popup flow.
 *
 * Click delegation in capture phase so any existing per-button listener
 * (third-party cart widgets, theme defaults) doesn't intercept first.
 * The handler reads two data-* attributes off the closest matching
 * `[data-jrs-edit-customization]` ancestor:
 *
 *   data-jrs-edit-customization  — the cart line item key (required)
 *   data-jrs-edit-quantity       — the cart line quantity (optional)
 *
 * If both are present (key is required; quantity may be empty/0 and
 * downstream callers will fall back to a fetched cart line), the
 * handler emits:
 *
 *   JRS.EventBus.emit('cart:editRequested', {
 *     lineItemKey: <string>,
 *     lineItemQuantity: <number|null>
 *   })
 *
 * Subscriber: jrs-v2-cart-edit-flow.js (commit 5 rewrite). The
 * subscriber calls JRS.CustomizerPopup.openFromCartLine(...) which is
 * already wired in Session 1 commit 3 / 60ff6cd + the Session 1
 * follow-up 4e0fe7e that fixed editMode plumbing.
 *
 * This module is render-agnostic: it doesn't care if the Edit button
 * is in the cart page, cart drawer, mini-cart, or anywhere else — as
 * long as the trigger element carries the data-jrs-edit-customization
 * attribute, this listener catches it.
 *
 * Commit 9 (separate session) adds the actual `<button
 * data-jrs-edit-customization="…">` render to cart-item.liquid. Until
 * then, this listener is a no-op because no element on the page
 * carries the attribute.
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  var SELECTOR = '[data-jrs-edit-customization]';
  var EVENT_NAME = 'cart:editRequested';
  var _bound = false;

  /**
   * Walk up from `el` looking for the nearest ancestor matching SELECTOR.
   * `el.closest` polyfill safe-guard for very old browsers; modern Shopify
   * customer base is on Chrome/Safari/Firefox where `closest` is present,
   * but the manual walk keeps ES5 + IE11-class fallbacks alive (theme has
   * other ES5-pattern modules for the same reason).
   *
   * @param {Element|EventTarget|null} el
   * @returns {Element|null}
   */
  function _closestEditTrigger(el) {
    if (!el) return null;
    if (typeof el.closest === 'function') {
      try { return el.closest(SELECTOR); } catch (_e) { /* fall through */ }
    }
    var node = el;
    while (node && node.nodeType === 1) {
      if (typeof node.matches === 'function' && node.matches(SELECTOR)) return node;
      // Older Edge / IE11 fallback name:
      if (typeof node.msMatchesSelector === 'function' && node.msMatchesSelector(SELECTOR)) return node;
      node = node.parentNode;
    }
    return null;
  }

  /**
   * Read a numeric data-* attribute. Returns null when missing or
   * unparseable so downstream code can decide whether to fall back to
   * a /cart.js fetch.
   *
   * @param {Element} el
   * @param {string} attrName
   * @returns {number|null}
   */
  function _readNumericAttr(el, attrName) {
    if (!el || !el.getAttribute) return null;
    var raw = el.getAttribute(attrName);
    if (raw === null || raw === '') return null;
    var n = parseInt(raw, 10);
    if (!isFinite(n) || n < 0) return null;
    return n;
  }

  /**
   * Capture-phase click handler. Bound to document so the event reaches
   * the listener BEFORE any per-button bubble-phase handler.
   *
   * @param {MouseEvent} e
   */
  function _onCartClick(e) {
    // Only react to primary-button (left-click / touch). Ignore modified
    // clicks (ctrl/cmd/shift) so middle-click "open in new tab" and
    // "save link" semantics keep working if the trigger is ever an <a>.
    if (e && e.defaultPrevented) return;
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    if (e && typeof e.button === 'number' && e.button !== 0) return;

    var trigger = _closestEditTrigger(e && e.target);
    if (!trigger) return;

    var lineItemKey = trigger.getAttribute('data-jrs-edit-customization');
    if (!lineItemKey) {
      // Attribute present but value empty — log so the markup author can
      // diagnose. (Commit 9 renders the value from `item.key`; an empty
      // value means upstream Liquid drift.)
      if (window.console && typeof console.warn === 'function') {
        console.warn(
          'jrs-v2-cart-edit-trigger: clicked [data-jrs-edit-customization] ' +
          'with empty value. Markup is broken; emitting nothing.'
        );
      }
      return;
    }

    var lineItemQuantity = _readNumericAttr(trigger, 'data-jrs-edit-quantity');

    // Suppress the default action (button submit, anchor navigation) so
    // the click stays in-page. Critical when the trigger is rendered
    // inside a <form> on cart.liquid where the cart-update form would
    // otherwise submit.
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

    if (!JRS.EventBus || typeof JRS.EventBus.emit !== 'function') {
      if (window.console && typeof console.warn === 'function') {
        console.warn(
          'jrs-v2-cart-edit-trigger: JRS.EventBus.emit unavailable. ' +
          'Tier 1 load order broken or event-bus module failed to define.'
        );
      }
      return;
    }

    JRS.EventBus.emit(EVENT_NAME, {
      lineItemKey: String(lineItemKey),
      lineItemQuantity: lineItemQuantity
    });
  }

  /**
   * Bind the document-level capture-phase listener. Idempotent — safe to
   * call multiple times; only the first binds.
   */
  function init() {
    if (_bound) return;
    if (typeof document === 'undefined' || !document.addEventListener) return;
    document.addEventListener('click', _onCartClick, /* useCapture */ true);
    _bound = true;
  }

  /**
   * Unbind for tests / hot-reload. Not exercised in production.
   */
  function destroy() {
    if (!_bound) return;
    if (typeof document === 'undefined' || !document.removeEventListener) return;
    document.removeEventListener('click', _onCartClick, /* useCapture */ true);
    _bound = false;
  }

  // Public surface — keeps init/destroy callable by tests if ever needed.
  JRS.CartEditTrigger = {
    init: init,
    destroy: destroy,
    // Internal hook for diagnostics / unit tests:
    _onCartClick: _onCartClick
  };

  // Auto-init at IIFE eval. The script is deferred + Tier 3 cart-only,
  // so by the time it runs the DOM is fully parsed; no DOMContentLoaded
  // wait needed.
  init();
})(window.JRS);
